import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Extension } from '@tiptap/react';
import type {
  CodingOverlayEvidenceLink,
  CodingOverlayProblem,
} from '../../contracts/coding-overlay-types';
import { findProblemsForSection } from './find-problems-for-section';
import {
  buildNormToOrigMapping,
  findSectionRange,
  findTextInDoc,
  findTextInRange,
  type TextMatchResult,
} from './text-match-anchor';

interface CodingOverlayMeta {
  hoveredProblemId: string | null;
}

interface CodingOverlayOptions {
  problems: CodingOverlayProblem[];
  /**
   * Opt-in per host editor: anchor chips/pills to top-level `sectionNode`
   * identity (`problemId` attr, falling back to heading-text matching) instead
   * of `buildDecorations`'s default Assessment & Plan name search. `false` for
   * every existing vJSON/vMarkdown host, which keeps the default anchoring.
   */
  matchSectionsByIdentity: boolean;
}

export interface PopoverState {
  el: HTMLDivElement | null;
  anchorEl: HTMLElement | null;
  removeListeners: (() => void) | null;
}

interface CodingOverlayStorage {
  problems: CodingOverlayProblem[];
  hoveredProblemId: string | null;
  popover: PopoverState;
  editorRoot: HTMLElement | null;
  matchSectionsByIdentity: boolean;
}

declare module '@tiptap/react' {
  interface Commands<ReturnType> {
    codingOverlay: {
      setCodingOverlayHover: (problemId: string | null) => ReturnType;
      setCodingOverlayProblems: (problems: CodingOverlayProblem[]) => ReturnType;
    };
  }
}

const PLUGIN_KEY = new PluginKey<DecorationSet>('codingOverlay');
const PROBLEM_HEADING_PREFIX_PATTERN = /^\s*(?:\d+[\.)]\s*|[-•*]\s*)?$/;

function hasProblemHeadingPrefix(prefix: string): boolean {
  return PROBLEM_HEADING_PREFIX_PATTERN.test(prefix);
}

function closePopover(popover: PopoverState) {
  if (popover.el) {
    popover.el.remove();
    popover.el = null;
  }
  popover.anchorEl = null;
  if (popover.removeListeners) {
    popover.removeListeners();
    popover.removeListeners = null;
  }
}

function createWaveformIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  svg.setAttribute('stroke-linecap', 'round');

  const bars = [
    { x: 3, y1: 6, y2: 10 },
    { x: 5.5, y1: 4, y2: 12 },
    { x: 8, y1: 2.5, y2: 13.5 },
    { x: 10.5, y1: 4, y2: 12 },
    { x: 13, y1: 6, y2: 10 },
  ];
  for (const bar of bars) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(bar.x));
    line.setAttribute('y1', String(bar.y1));
    line.setAttribute('x2', String(bar.x));
    line.setAttribute('y2', String(bar.y2));
    svg.appendChild(line);
  }
  return svg;
}

function createContextIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.5');
  svg.setAttribute('stroke-linecap', 'round');

  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  circle.setAttribute('cx', '8');
  circle.setAttribute('cy', '8');
  circle.setAttribute('r', '5.5');
  svg.appendChild(circle);

  const innerCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
  innerCircle.setAttribute('cx', '8');
  innerCircle.setAttribute('cy', '8');
  innerCircle.setAttribute('r', '2');
  svg.appendChild(innerCircle);

  const crosshairs = [
    { x1: 8, y1: 1, x2: 8, y2: 3 },
    { x1: 8, y1: 13, x2: 8, y2: 15 },
    { x1: 1, y1: 8, x2: 3, y2: 8 },
    { x1: 13, y1: 8, x2: 15, y2: 8 },
  ];
  for (const ch of crosshairs) {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(ch.x1));
    line.setAttribute('y1', String(ch.y1));
    line.setAttribute('x2', String(ch.x2));
    line.setAttribute('y2', String(ch.y2));
    svg.appendChild(line);
  }
  return svg;
}

function appendEvidenceBox(parent: HTMLElement, text: string, icon: SVGSVGElement) {
  const box = document.createElement('div');
  box.className = 'coding-overlay-popover-evidence';

  const iconWrapper = document.createElement('span');
  iconWrapper.className = 'coding-overlay-popover-evidence-icon';
  iconWrapper.appendChild(icon);

  const quote = document.createElement('p');
  quote.className = 'coding-overlay-popover-evidence-quote';
  quote.textContent = `\u201C${text}\u201D`;

  box.appendChild(iconWrapper);
  box.appendChild(quote);
  parent.appendChild(box);
}

/**
 * Show (or toggle) an evidence popover anchored to an ICD-10 pill element.
 *
 * Creates a floating DOM element in `document.body` with the clinical
 * description and any transcript/context evidence, then installs global
 * listeners (pointerdown, keydown, resize, scroll) to reposition or
 * dismiss it. If the same anchor is clicked again the popover is closed.
 *
 * @param popover - Mutable per-editor popover state (element, anchor, listener cleanup).
 * @param payload.anchorEl - The pill DOM element to anchor the popover to.
 * @param payload.clinicalDescription - Primary text shown at the top of the popover.
 * @param payload.transcriptExcerpt - Fallback evidence if no evidence_links exist.
 * @param payload.evidenceLinks - Transcript and context evidence entries to render.
 * @param payload.problemId - Problem identifier used as a data attribute.
 * @param payload.code - ICD-10 code string (currently used for keying, not displayed).
 */
function openPillPopover(
  popover: PopoverState,
  payload: {
    anchorEl: HTMLElement;
    clinicalDescription: string;
    transcriptExcerpt: string | null;
    evidenceLinks: CodingOverlayEvidenceLink[];
    problemId: string;
    code: string;
  }
) {
  if (popover.anchorEl === payload.anchorEl && popover.el) {
    closePopover(popover);
    return;
  }

  closePopover(popover);

  const popoverEl = document.createElement('div');
  popoverEl.className = 'coding-overlay-popover';
  popoverEl.dataset.problemId = payload.problemId;

  const description = document.createElement('p');
  description.className = 'coding-overlay-popover-description';
  description.textContent = payload.clinicalDescription;
  popoverEl.appendChild(description);

  const transcriptLinks = payload.evidenceLinks.filter((l) => l.source === 'transcript');
  const contextLinks = payload.evidenceLinks.filter((l) => l.source === 'context_tab');

  for (const link of transcriptLinks) {
    appendEvidenceBox(popoverEl, link.text, createWaveformIcon());
  }
  for (const link of contextLinks) {
    appendEvidenceBox(popoverEl, link.text, createContextIcon());
  }

  if (
    transcriptLinks.length === 0 &&
    contextLinks.length === 0 &&
    payload.transcriptExcerpt
  ) {
    appendEvidenceBox(popoverEl, payload.transcriptExcerpt, createWaveformIcon());
  }

  document.body.appendChild(popoverEl);
  popover.el = popoverEl;
  popover.anchorEl = payload.anchorEl;

  const reposition = () => {
    if (!popover.el || !popover.anchorEl) return;
    if (!popover.anchorEl.isConnected) {
      closePopover(popover);
      return;
    }
    const anchorRect = popover.anchorEl.getBoundingClientRect();
    const popoverRect = popover.el.getBoundingClientRect();
    const maxLeft = Math.max(8, window.innerWidth - popoverRect.width - 8);
    const left = Math.min(Math.max(8, anchorRect.left), maxLeft);
    const spaceBelow = window.innerHeight - anchorRect.bottom - 8;
    const top =
      spaceBelow >= popoverRect.height
        ? anchorRect.bottom + 8
        : anchorRect.top - popoverRect.height - 8;
    popover.el.style.left = `${left}px`;
    popover.el.style.top = `${top}px`;
  };
  reposition();

  const onPointerDown = (event: Event) => {
    const targetNode = event.target as Node;
    if (!popover.el || !popover.anchorEl) return;
    if (popover.el.contains(targetNode) || popover.anchorEl.contains(targetNode)) {
      return;
    }
    closePopover(popover);
  };
  const onEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closePopover(popover);
    }
  };
  const onResize = () => reposition();
  const onScroll = () => reposition();

  document.addEventListener('pointerdown', onPointerDown, true);
  document.addEventListener('keydown', onEscape, true);
  window.addEventListener('resize', onResize);
  window.addEventListener('scroll', onScroll, true);
  popover.removeListeners = () => {
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('keydown', onEscape, true);
    window.removeEventListener('resize', onResize);
    window.removeEventListener('scroll', onScroll, true);
  };
}

/**
 * Find the end-of-block position for a problem in the A&P section.
 * Walks forward from `afterPos` to the last block boundary before
 * the next numbered problem heading or the section end.
 */
export function findProblemBlockEnd(
  doc: ProseMirrorNode,
  afterPos: number,
  nextProblemMatch: TextMatchResult | null,
  sectionEnd: number
): number {
  if (!nextProblemMatch) {
    // Last problem: find the end of the last textblock inside the section
    // rather than using sectionEnd (which may be past the wrapper node boundary).
    let lastBlockEnd = afterPos;
    doc.nodesBetween(afterPos, sectionEnd, (node, pos) => {
      // Check both textblocks and list items
      if (node.isTextblock || node.type.name === 'listItem') {
        const end = pos + node.nodeSize;
        if (end > lastBlockEnd && end <= sectionEnd) lastBlockEnd = end;
      }
    });
    return lastBlockEnd > afterPos ? lastBlockEnd : sectionEnd;
  }
  const boundary = Math.min(nextProblemMatch.from, sectionEnd);
  if (boundary <= afterPos) return afterPos;

  // Find the END of the last block (textblock, listItem, or any block) whose
  // range sits fully before the next problem's match.
  let pos = afterPos;
  doc.nodesBetween(afterPos, boundary, (node, nodePos) => {
    // Include listItems and other block types, not just textblocks
    if (node.isBlock && node.content.size > 0) {
      const end = nodePos + node.nodeSize;
      if (end <= boundary && end > pos) pos = end;
    }
  });

  // If we didn't find any blocks (pos unchanged), place the widget at the
  // boundary itself rather than subtracting 1 (which could land inside a
  // node's opening token and cause ProseMirror to throw).
  if (pos === afterPos && boundary > afterPos) {
    pos = boundary;
  }

  return pos;
}

/**
 * Apply highlight decorations per-textblock so they don't break across
 * block boundaries (ProseMirror limitation on inline decorations).
 */
function addBlockHighlights(
  doc: ProseMirrorNode,
  from: number,
  to: number,
  cssClass: string,
  problemId: string,
  decorations: Decoration[]
) {
  doc.nodesBetween(from, to, (node, pos) => {
    if (node.isTextblock && node.content.size > 0) {
      const blockStart = pos + 1;
      const blockEnd = pos + 1 + node.content.size;
      const clampedFrom = Math.max(blockStart, from);
      const clampedTo = Math.min(blockEnd, to);
      if (clampedFrom < clampedTo) {
        decorations.push(
          Decoration.inline(clampedFrom, clampedTo, {
            class: cssClass,
            'data-problem-id': problemId,
          })
        );
      }
    }
  });
}

export function getMeatHighlightClass(
  meatCategory: CodingOverlayEvidenceLink['meat_category'] | undefined
): string | null {
  if (meatCategory === 'treat') return 'coding-overlay-highlight-primary';
  return null;
}

/**
 * Build per-excerpt inline decorations from a problem's evidence_links.
 * Returns true if at least one MEAT treat evidence link matched and
 * produced a highlight decoration, false if no highlight decorations
 * were applied (e.g. no evidence links, no `source === 'note'` links,
 * no `meat_category === 'treat'` links, or no text matches in the doc).
 */
export function buildEvidenceHighlights(
  problem: CodingOverlayProblem,
  doc: ProseMirrorNode,
  decorations: Decoration[],
  blockStart?: number,
  blockEnd?: number
): boolean {
  const links = problem.evidence_links;
  if (!links || links.length === 0) return false;

  const noteLinks = links.filter((l) => l.source === 'note');
  if (noteLinks.length === 0) return false;

  let matched = false;
  for (const link of noteLinks) {
    const cssClass = getMeatHighlightClass(link.meat_category);
    if (!cssClass) continue;

    // When block boundaries are provided, use range-scoped search.
    // Otherwise fall back to section-based search (legacy path).
    if (blockStart !== undefined && blockEnd !== undefined) {
      const found = findTextInRange(doc, link.text, blockStart, blockEnd);
      if (found) {
        matched = true;
        addBlockHighlights(
          doc,
          found.from,
          found.to,
          cssClass,
          problem.problem_id,
          decorations
        );
        continue;
      }

      // Try line-by-line matching within block boundaries
      const lines = link.text
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (lines.length > 1) {
        for (const line of lines) {
          const lineMatch = findTextInRange(doc, line, blockStart, blockEnd);
          if (lineMatch) {
            matched = true;
            addBlockHighlights(
              doc,
              lineMatch.from,
              lineMatch.to,
              cssClass,
              problem.problem_id,
              decorations
            );
          }
        }
      }
      continue;
    }

    // Legacy path: section-based search (no block boundaries provided)
    // Remove null fallback - only search within Assessment section
    const sectionHints = [link.section, 'Assessment'].filter(
      (v, i, a) => v != null && a.indexOf(v) === i
    ) as string[];

    let foundFull = false;
    for (const hint of sectionHints) {
      const found = findTextInDoc(doc, link.text, hint);
      if (found) {
        matched = true;
        foundFull = true;
        addBlockHighlights(
          doc,
          found.from,
          found.to,
          cssClass,
          problem.problem_id,
          decorations
        );
        break;
      }
    }
    if (foundFull) continue;

    // Evidence text often spans multiple paragraphs — try each line individually
    const lines = link.text
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length <= 1) continue;

    for (const line of lines) {
      for (const hint of sectionHints) {
        const lineMatch = findTextInDoc(doc, line, hint);
        if (lineMatch) {
          matched = true;
          addBlockHighlights(
            doc,
            lineMatch.from,
            lineMatch.to,
            cssClass,
            problem.problem_id,
            decorations
          );
          break;
        }
      }
    }
  }

  return matched;
}

/**
 * The inline problem-name chip DOM, shared by `buildDecorations`'s A&P-search
 * anchoring and `buildSectionIdentityDecorations`'s per-section anchoring so
 * the two strategies can never render a different-looking chip.
 */
function createProblemChipElement(
  problem: CodingOverlayProblem,
  renderChipUnderHeading: boolean
): HTMLElement {
  const problemChip = document.createElement('span');
  problemChip.className = 'coding-overlay-problem-chip-inline';
  if (renderChipUnderHeading) {
    // Render as its own row under the heading rather than inline-trailing text.
    problemChip.classList.add('coding-overlay-chip-header-row');
  }

  const problemIcon = document.createElement('span');
  problemIcon.className = 'coding-overlay-problem-chip-icon';
  const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  iconSvg.setAttribute('viewBox', '0 0 16 16');
  iconSvg.setAttribute('width', '11');
  iconSvg.setAttribute('height', '11');
  iconSvg.setAttribute('aria-hidden', 'true');
  iconSvg.setAttribute('fill', 'none');
  iconSvg.setAttribute('stroke', 'currentColor');
  iconSvg.setAttribute('stroke-width', '1.6');
  iconSvg.setAttribute('stroke-linecap', 'round');
  iconSvg.setAttribute('stroke-linejoin', 'round');

  const outer = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  outer.setAttribute('x', '1.5');
  outer.setAttribute('y', '1.5');
  outer.setAttribute('width', '13');
  outer.setAttribute('height', '13');
  outer.setAttribute('rx', '2');

  const vertical = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  vertical.setAttribute('x1', '8');
  vertical.setAttribute('y1', '2.5');
  vertical.setAttribute('x2', '8');
  vertical.setAttribute('y2', '13.5');

  const horizontalTop = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  horizontalTop.setAttribute('x1', '2.5');
  horizontalTop.setAttribute('y1', '6');
  horizontalTop.setAttribute('x2', '13.5');
  horizontalTop.setAttribute('y2', '6');

  const horizontalBottom = document.createElementNS('http://www.w3.org/2000/svg', 'line');
  horizontalBottom.setAttribute('x1', '2.5');
  horizontalBottom.setAttribute('y1', '10');
  horizontalBottom.setAttribute('x2', '13.5');
  horizontalBottom.setAttribute('y2', '10');

  iconSvg.appendChild(outer);
  iconSvg.appendChild(vertical);
  iconSvg.appendChild(horizontalTop);
  iconSvg.appendChild(horizontalBottom);
  problemIcon.appendChild(iconSvg);

  const problemText = document.createElement('span');
  problemText.className = 'coding-overlay-problem-chip-text';
  problemText.textContent = problem.name;

  problemChip.appendChild(problemIcon);
  problemChip.appendChild(problemText);
  return problemChip;
}

function createProblemChipDecoration(
  problem: CodingOverlayProblem,
  chipPos: number,
  chipSide: -1 | 1,
  renderChipUnderHeading: boolean
): Decoration {
  return Decoration.widget(
    chipPos,
    () => createProblemChipElement(problem, renderChipUnderHeading),
    { side: chipSide, key: `problem-chip-inline-${problem.problem_id}` }
  );
}

/** Shimmer placeholder shown while a problem's ICD-10 codes are still generating. */
function createLoadingRowElement(): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'coding-overlay-block';

  const row = document.createElement('div');
  row.className = 'coding-overlay-pills';

  const pill = document.createElement('span');
  pill.className = 'coding-overlay-pill-loading';

  const dots = document.createElement('span');
  dots.className = 'coding-overlay-pill-loading-dots';
  dots.textContent = '…';

  const bar = document.createElement('span');
  bar.className = 'coding-overlay-pill-loading-bar';

  pill.appendChild(dots);
  pill.appendChild(bar);
  row.appendChild(pill);

  const hr = document.createElement('div');
  hr.className = 'coding-overlay-divider';

  wrapper.appendChild(row);
  wrapper.appendChild(hr);
  return wrapper;
}

/** Clickable ICD-10 pill row + popover-on-click wiring for a problem's codes. */
function createPillsRowElement(
  problem: CodingOverlayProblem,
  icd10Codes: CodingOverlayProblem['icd10_codes'],
  popoverState: PopoverState,
  getEditorRoot: () => HTMLElement | null
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'coding-overlay-block';

  const row = document.createElement('div');
  row.className = 'coding-overlay-pills';
  row.dataset.problemId = problem.problem_id;

  for (const code of icd10Codes) {
    const item = document.createElement('span');
    item.className = 'coding-overlay-pill-item';
    item.dataset.problemId = problem.problem_id;
    if (code.rationale) item.title = code.rationale;
    item.addEventListener('mouseenter', () => {
      document.dispatchEvent(
        new CustomEvent('coding-overlay-pill-hover', {
          detail: { problemId: problem.problem_id, editorRoot: getEditorRoot() },
        })
      );
    });
    item.addEventListener('mouseleave', () => {
      document.dispatchEvent(
        new CustomEvent('coding-overlay-pill-hover', {
          detail: { problemId: null, editorRoot: getEditorRoot() },
        })
      );
    });
    item.addEventListener('click', () => {
      const transcriptExcerpt = code.transcript_excerpt ?? null;
      const clinicalDescription =
        code.rationale ??
        problem.assessment_text?.trim() ??
        `${problem.name}: ${code.description}`;
      openPillPopover(popoverState, {
        anchorEl: item,
        clinicalDescription,
        transcriptExcerpt,
        evidenceLinks: problem.evidence_links ?? [],
        problemId: problem.problem_id,
        code: code.code,
      });
    });

    const badge = document.createElement('span');
    badge.className = 'coding-overlay-icd10-badge';
    badge.textContent = `ICD ${code.code}`;

    const desc = document.createElement('span');
    desc.className = 'coding-overlay-pill-desc';
    desc.textContent = code.description;

    item.appendChild(badge);
    item.appendChild(desc);
    row.appendChild(item);
  }

  const hr = document.createElement('div');
  hr.className = 'coding-overlay-divider';

  wrapper.appendChild(row);
  wrapper.appendChild(hr);

  return wrapper;
}

/**
 * The ICD-10 codes widget for a single problem: `null` when enrichment has
 * finished and this problem got no codes (nothing to show), the shimmer
 * placeholder while codes are still pending, or the pills row once they land.
 */
function createCodesDecoration(
  problem: CodingOverlayProblem,
  blockEnd: number,
  popoverState: PopoverState,
  getEditorRoot: () => HTMLElement | null,
  enrichmentComplete: boolean
): Decoration | null {
  const icd10Codes = problem.icd10_codes ?? [];
  if (icd10Codes.length === 0 && enrichmentComplete) return null;
  if (icd10Codes.length === 0) {
    return Decoration.widget(blockEnd, createLoadingRowElement, {
      side: -1,
      key: `icd10-loading-${problem.problem_id}`,
    });
  }
  return Decoration.widget(
    blockEnd,
    () => createPillsRowElement(problem, icd10Codes, popoverState, getEditorRoot),
    { side: -1, key: `icd10-pills-${problem.problem_id}` }
  );
}

/**
 * Build the full ProseMirror DecorationSet for the coding overlay.
 *
 * For each problem, locates its numbered heading in the Assessment section,
 * then creates:
 *  - an inline problem-name chip widget after the heading,
 *  - shimmer loading placeholders (when ICD-10 codes are pending), or
 *    clickable ICD-10 pill widgets with popover evidence on click,
 *  - per-excerpt MEAT highlight decorations on coding-overlay hover
 *    (no highlight is drawn when treatment evidence links are absent).
 *
 * @param problems - Current session problems with optional ICD-10 codes and evidence.
 * @param hoveredProblemId - Problem currently hovered in the coding overlay, or `null`.
 * @param doc - The ProseMirror document node to decorate.
 * @param popoverState - Mutable per-editor popover state, threaded through so
 *   pill click handlers can open/close the evidence popover.
 * @returns A DecorationSet to be applied by the plugin.
 */
export function buildDecorations(
  problems: CodingOverlayProblem[],
  hoveredProblemId: string | null,
  doc: ProseMirrorNode,
  popoverState: PopoverState,
  getEditorRoot: () => HTMLElement | null
): DecorationSet {
  // Fix 7: nothing to decorate in an empty document
  if (doc.content.size === 0) return DecorationSet.empty;

  // A non-PBC note now always carries an inert coding overlay (installed at
  // editor creation so the flag flip never tears the editor down — see
  // NoteTabPane's `markdownNativeCodingOverlayExtension`). With no problems
  // there is nothing to anchor, so bail before the Assessment/Plan section
  // scans below — this keeps the per-keystroke cost at zero for those notes.
  if (problems.length === 0) return DecorationSet.empty;

  const decorations: Decoration[] = [];

  const assessmentRange = findSectionRange(doc, 'Assessment');
  const planRange = findSectionRange(doc, 'Plan');
  const hasAnyRange = assessmentRange !== null || planRange !== null;

  // Combined search bounds covering both Assessment and Plan (whichever exist in this doc).
  // JSON section editors contain only the section body — no heading — so both are null and
  // we fall back to the full document, which is just that one section's content.
  const searchFrom = hasAnyRange
    ? Math.min(
        assessmentRange?.from ?? doc.content.size,
        planRange?.from ?? doc.content.size
      )
    : 0;
  const searchTo = hasAnyRange
    ? Math.max(assessmentRange?.to ?? 0, planRange?.to ?? 0)
    : doc.content.size;

  // For single-problem editors (new PBC format where each section = one problem), always
  // use the full document as the widget placement boundary. Sub-headings like "Plan:" or
  // "Medications:" inside plan text would otherwise cause findSectionRange to return a
  // mid-document range, misplacing the chip/pill widgets.
  const sectionEnd = problems.length === 1 ? doc.content.size : searchTo;

  // Fix 5: multi-problem full notes must have at least one section range. Searching the
  // entire document would match problem names in unrelated sections (HPI, ROS, etc.).
  // Single-problem JSON section editors have no headings by design — full-doc is correct.
  if (!hasAnyRange && problems.length > 1) {
    return DecorationSet.empty;
  }

  // Find ALL occurrences of each problem name and pick the one at block start
  const headingMatches: (TextMatchResult | null)[] = problems.map((p) => {
    // Find all occurrences of the problem name within the Assessment/Plan range (or full doc)
    const matches: TextMatchResult[] = [];
    const nameNeedle = p.name.toLowerCase().replace(/\s+/g, ' ').trim();
    // Per-line normalised plan_text; each line has bullet prefix stripped for Needle 2 matching.
    const planLineNorms = (p.plan_text ?? '')
      .split('\n')
      .map((l) =>
        l
          .replace(/\s+/g, ' ')
          .toLowerCase()
          .trim()
          .replace(/^[-•*]\s+/, '')
      )
      .filter((l) => l.length > 0);

    doc.nodesBetween(searchFrom, searchTo, (node, pos) => {
      if (node.isTextblock) {
        const blockText = node.textContent;
        const normalisedBlock = blockText.replace(/\s+/g, ' ').toLowerCase();
        const normToOrig = buildNormToOrigMapping(blockText);

        // Needle 1: problem name — full span, chip placed after the name
        {
          let searchStart = 0;
          let idx: number;
          while ((idx = normalisedBlock.indexOf(nameNeedle, searchStart)) !== -1) {
            const origIdx = normToOrig[idx];
            const origEndIdx = normToOrig[idx + nameNeedle.length];
            if (origIdx === undefined || origEndIdx === undefined) {
              searchStart = idx + 1;
              continue;
            }
            const matchFrom = pos + 1 + origIdx;
            const matchTo = pos + 1 + origEndIdx;
            if (hasProblemHeadingPrefix(blockText.slice(0, origIdx))) {
              matches.push({ from: matchFrom, to: matchTo });
            }
            searchStart = idx + 1;
          }
        }

        // Needle 2: block content matches any individual line of plan_text — zero-width match
        // so the chip appears before the plan content and the full block falls inside the
        // evidence range. Checks every plan_text line (not just the first) so problems can
        // anchor on any plan item regardless of ordering in the document.
        const blockNorm = normalisedBlock.trim().replace(/^[-•*]\s+/, '');
        if (blockNorm && planLineNorms.length > 0) {
          if (
            planLineNorms.some(
              (line) =>
                line === blockNorm ||
                line.startsWith(blockNorm) ||
                blockNorm.startsWith(line)
            )
          ) {
            matches.push({ from: pos + 1, to: pos + 1 });
          }
        }
      }
    });

    // Return the first block-start match, or null if none found
    return matches.length > 0 ? matches[0]! : null;
  });

  // Deduplicate: if two problems match at the same position, keep only the first
  const claimedPositions = new Set<number>();
  const nameMatches: (TextMatchResult | null)[] = headingMatches.map((m) => {
    if (!m) return null;
    if (claimedPositions.has(m.from)) return null;
    claimedPositions.add(m.from);
    return m;
  });

  // Build array of {index, problem, match} and sort by document position
  // This ensures we process problems in document order, not array order
  const problemsWithMatches = problems.map((p, i) => ({
    index: i,
    problem: p,
    match: nameMatches[i],
  }));

  // Sort by document position (problems without matches go to the end)
  const sortedProblems = [...problemsWithMatches].sort((a, b) => {
    if (!a.match && !b.match) return 0;
    if (!a.match) return 1;
    if (!b.match) return -1;
    return a.match.from - b.match.from;
  });

  const enrichmentComplete = problems.some((p) => (p.icd10_codes ?? []).length > 0);

  for (let idx = 0; idx < sortedProblems.length; idx++) {
    const { problem, match } = sortedProblems[idx]!;

    // When the section title IS the problem name (new PBC format) the name
    // won't appear in the editor content, so both needles miss and match is
    // null.  Fall back to position 1 (start of first block) so the chip and
    // pills still render at the end of the section content.
    const effectiveMatch = match ?? { from: 1, to: 1 };

    // Find the next problem in document order (from sorted list)
    const nextInDocOrder = sortedProblems[idx + 1]?.match ?? null;
    const blockEnd = findProblemBlockEnd(
      doc,
      effectiveMatch.to,
      nextInDocOrder,
      sectionEnd
    );

    // Problem chip at heading — try numbered format first, fall back to
    // the validated block-start match. The match variable already passed
    // the "is at block start" filter, so it's safe to use as a fallback.
    // Use document-order rank (idx+1) not original array index (i+1).
    const numberedHeading = `${idx + 1}. ${problem.name}`;
    let headingMatch = findTextInDoc(doc, numberedHeading, 'Assessment');
    if (!headingMatch) {
      // No numbered heading found — use the effective match position (which
      // may be the doc-start fallback when the name is the section title).
      headingMatch = effectiveMatch;
    }
    if (headingMatch) {
      // When the problem name is the section title (single-problem JSON sectional PBC
      // format) it never appears inline, so headingMatch is a zero-width fallback. Anchor the
      // chip at position 0 — before the first block — so it renders as a block-level row
      // directly under the section heading, rather than at the block end with the ICD pills.
      // Position 0 (not 1) keeps the chip above the section body whether it starts with a
      // paragraph or a list (pos 1 lands inside the first list item); side:-1 associates it
      // with the preceding node's marks (none at doc start) so it can't inherit bold/italic
      // from the following text. Multi-problem notes keep their prior placement (inline at
      // headingMatch.to, or blockEnd for zero-width plan-section anchors) so chips never stack.
      //
      // Editor context: the sectional (zero-width) path is the live Athena PBC surface today
      // (vJSON / useJSONSectionEditor). The inline headingMatch path serves the full-note
      // editors — v1 (legacy Athena, still live) and vMarkdown (future cutover) — so it is
      // intentionally retained, not dead code.
      const isZeroWidthMatch = headingMatch.from === headingMatch.to;
      const renderChipUnderHeading = isZeroWidthMatch && problems.length === 1;
      const chipPos = renderChipUnderHeading
        ? 0
        : isZeroWidthMatch
          ? blockEnd
          : headingMatch.to;
      const chipSide = isZeroWidthMatch ? -1 : 1;
      decorations.push(
        createProblemChipDecoration(problem, chipPos, chipSide, renderChipUnderHeading)
      );
    }

    const codesDecoration = createCodesDecoration(
      problem,
      blockEnd,
      popoverState,
      getEditorRoot,
      enrichmentComplete
    );
    if (codesDecoration) decorations.push(codesDecoration);

    // Highlight on coding-overlay hover: only per-excerpt evidence links with
    // MEAT treatment category. When none are available, show no highlight
    // rather than blanketing the entire section.
    // Pass block boundaries to scope highlight matching to this problem only.
    if (hoveredProblemId === problem.problem_id) {
      buildEvidenceHighlights(problem, doc, decorations, effectiveMatch.from, blockEnd);
    }
  }

  return DecorationSet.create(doc, decorations);
}

/**
 * NOTE: currently unused under Option B — every host configures
 * `matchSectionsByIdentity: false`, so `buildDecorations` (A&P name search)
 * always runs and this identity path is never reached. Kept intentionally.
 *
 * Alternate anchoring strategy for hosts whose document is already segmented
 * into one `sectionNode` per problem (the backend emits N distinct
 * `# <Problem>` sections rather than a single combined Assessment & Plan block
 * holding every problem). `buildDecorations` keeps its A&P name-search behavior
 * for every other host; this path is opt-in per host editor via
 * `CodingOverlay.configure({ matchSectionsByIdentity: true })` so
 * vJSON/vMarkdown can never reach it.
 *
 * Each top-level `sectionNode` is matched to a problem by its `problemId`
 * attribute first — the durable join once `SectionLink.problem_id` is
 * populated — falling back to `findProblemsForSection` heading-text matching
 * when the attribute is absent. The chip renders directly under the section
 * heading and the ICD-10 pills/shimmer render at the end of the section — the
 * same layout `buildDecorations` uses for a single-problem editor, scoped here
 * to each section's own bounds instead of the whole editor.
 */
export function buildSectionIdentityDecorations(
  problems: CodingOverlayProblem[],
  hoveredProblemId: string | null,
  doc: ProseMirrorNode,
  popoverState: PopoverState,
  getEditorRoot: () => HTMLElement | null
): DecorationSet {
  if (doc.content.size === 0 || problems.length === 0) return DecorationSet.empty;

  const decorations: Decoration[] = [];
  const enrichmentComplete = problems.some((p) => (p.icd10_codes ?? []).length > 0);
  const claimedProblemIds = new Set<string>();

  doc.forEach((node, pos) => {
    if (node.type.name !== 'sectionNode') return;

    const heading = node.firstChild;
    if (!heading || heading.type.name !== 'heading') return;

    const attrs = node.attrs as Record<string, unknown>;
    const problemId = typeof attrs.problemId === 'string' ? attrs.problemId : null;

    let problem = problemId
      ? problems.find((p) => p.problem_id === problemId)
      : undefined;

    if (!problem) {
      const sectionTitle =
        typeof attrs.sectionTitle === 'string' ? attrs.sectionTitle : heading.textContent;
      const matched = findProblemsForSection({
        sectionKey: sectionTitle,
        sectionContent: node.textContent,
        problems,
      });
      problem = matched?.[0];
    }

    if (!problem || claimedProblemIds.has(problem.problem_id)) return;
    claimedProblemIds.add(problem.problem_id);

    // Directly under the heading, above the section body.
    const chipPos = pos + 1 + heading.nodeSize;
    decorations.push(createProblemChipDecoration(problem, chipPos, -1, true));

    // End of THIS section's own content, not the whole document.
    const blockEnd = pos + node.nodeSize - 1;
    const codesDecoration = createCodesDecoration(
      problem,
      blockEnd,
      popoverState,
      getEditorRoot,
      enrichmentComplete
    );
    if (codesDecoration) decorations.push(codesDecoration);

    if (hoveredProblemId === problem.problem_id) {
      buildEvidenceHighlights(problem, doc, decorations, pos + 1, blockEnd);
    }
  });

  return DecorationSet.create(doc, decorations);
}

export const CodingOverlay = Extension.create<CodingOverlayOptions, CodingOverlayStorage>(
  {
    name: 'codingOverlay',

    addOptions() {
      return {
        problems: [],
        matchSectionsByIdentity: false,
      };
    },

    addStorage() {
      return {
        problems: [],
        hoveredProblemId: null,
        popover: { el: null, anchorEl: null, removeListeners: null },
        editorRoot: null,
        matchSectionsByIdentity: this.options.matchSectionsByIdentity,
      };
    },

    addCommands() {
      return {
        setCodingOverlayHover:
          (problemId: string | null) =>
          ({ tr, dispatch }) => {
            if (dispatch) {
              dispatch(
                tr.setMeta(PLUGIN_KEY, {
                  hoveredProblemId: problemId,
                } satisfies CodingOverlayMeta)
              );
            }
            this.storage.hoveredProblemId = problemId;
            return true;
          },
        setCodingOverlayProblems:
          (problems: CodingOverlayProblem[]) =>
          ({ tr, dispatch }) => {
            this.storage.problems = problems;
            if (dispatch) {
              dispatch(
                tr.setMeta(PLUGIN_KEY, {
                  hoveredProblemId: this.storage.hoveredProblemId,
                })
              );
            }
            return true;
          },
      };
    },

    addProseMirrorPlugins() {
      const storage = this.storage;

      const build = (doc: ProseMirrorNode) =>
        (storage.matchSectionsByIdentity
          ? buildSectionIdentityDecorations
          : buildDecorations)(
          storage.problems,
          storage.hoveredProblemId,
          doc,
          storage.popover,
          () => storage.editorRoot
        );

      return [
        new Plugin<DecorationSet>({
          key: PLUGIN_KEY,
          state: {
            init: (_, state) => {
              return build(state.doc);
            },
            apply: (tr, oldDecorations, _oldState, newState) => {
              const meta = tr.getMeta(PLUGIN_KEY) as CodingOverlayMeta | undefined;

              if (meta) {
                storage.hoveredProblemId = meta.hoveredProblemId;
                closePopover(storage.popover);
                return build(newState.doc);
              }

              if (tr.docChanged) {
                closePopover(storage.popover);
                return build(newState.doc);
              }

              return oldDecorations.map(tr.mapping, newState.doc);
            },
          },
          props: {
            decorations(state) {
              return this.getState(state);
            },
          },
          view: (editorView) => {
            storage.editorRoot = editorView.dom;
            const onPillHover = (event: Event) => {
              const custom = event as CustomEvent<{
                problemId: string | null;
                editorRoot?: Node;
              }>;
              // Ignore events originating from sibling editor instances
              if (custom.detail?.editorRoot !== editorView.dom) return;
              editorView.dispatch(
                editorView.state.tr.setMeta(PLUGIN_KEY, {
                  hoveredProblemId: custom.detail?.problemId ?? null,
                } satisfies CodingOverlayMeta)
              );
            };
            document.addEventListener('coding-overlay-pill-hover', onPillHover);
            return {
              destroy: () => {
                storage.editorRoot = null;
                closePopover(storage.popover);
                document.removeEventListener('coding-overlay-pill-hover', onPillHover);
              },
            };
          },
        }),
      ];
    },
  }
);
